import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { $api, fetchClient, invalidate } from "./client";
import type { components } from "./schema";

export type Supplier = components["schemas"]["Supplier"];

/**
 * Paginated, searchable. Supplier is unbounded (a tenant may have thousands),
 * so this mirrors `useItems`: a paginated envelope, server-side search, and
 * `id__in` to resolve a selection. There is deliberately NO load-all id→name
 * resolver — names are denormalized into the payloads that need them (e.g.
 * `PlannedOrder.supplierName`).
 */
export function useSuppliers({
  ids,
  ...query
}: {
  search?: string;
  ordering?: string;
  ids?: number[];
  limit?: number;
  offset?: number;
} = {}) {
  return $api.useQuery(
    "get",
    "/api/v1/suppliers/",
    { params: { query: { ...query, id__in: ids } } },
    {
      // An explicitly-empty ids array means "resolve to nothing": don't fetch.
      enabled: ids === undefined || ids.length > 0,
      placeholderData: keepPreviousData,
    },
  );
}

const invalidateSuppliers = (queryClient: ReturnType<typeof useQueryClient>) =>
  invalidate(queryClient, "/api/v1/suppliers/");

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { code: string; name: string }) =>
      (await fetchClient.POST("/api/v1/suppliers/", { body })).data!,
    onSuccess: () => invalidateSuppliers(queryClient),
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: number;
      code?: string;
      name?: string;
    }) =>
      (
        await fetchClient.PATCH("/api/v1/suppliers/{id}/", {
          params: { path: { id } },
          body,
        })
      ).data!,
    onSuccess: () => invalidateSuppliers(queryClient),
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchClient.DELETE("/api/v1/suppliers/{id}/", {
        params: { path: { id } },
      }),
    onSuccess: () => invalidateSuppliers(queryClient),
  });
}
